/**
 * Notification Settings — /(app)/settings/notifications
 *
 * Per channel (Push, Email) there's a MASTER switch. When it's on, a small set
 * of GROUP switches (Your turn, Game updates, Game invites, Secret messages, Inbox messages)
 * appears beneath it; when it's off they're hidden — but their values are
 * preserved (server-side + locally), so flipping the master back on restores
 * the previous choices. Everything is on by default.
 *
 * The groups are a UI convenience only: the wire protocol is still per-TYPE
 * (see NOTIFICATION_GROUPS in @warcarp/api-types). A group switch flips every
 * type it covers at once, and reads "on" iff all its members are on. Each
 * change writes to PATCH /api/v1/users/me optimistically; on error it rolls
 * back and a toast explains.
 *
 * Transactional email (confirm / reset / email-change) is always sent and is
 * intentionally not listed here.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React, { useState } from 'react'
import { View, Text, ScrollView, Switch, TouchableOpacity, ActivityIndicator, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { useAuth } from '../../../hooks/useAuth'
import { apiPatch } from '../../../lib/api'
import { goBackOr } from '../../../lib/goBack'
import { useToast } from '../../../components/ui/Toast'
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
  NOTIFICATION_GROUPS,
  type NotificationChannel,
  type NotificationGroup,
  type NotificationSettings,
  type NotificationType,
  type NotificationTypeToggles,
  type UserResponse,
} from '@warcarp/api-types'

const CHANNELS: Array<{ key: NotificationChannel; masterKey: 'pushNotifications' | 'emailNotifications'; title: string; blurb: string }> = [
  { key: 'push', masterKey: 'pushNotifications', title: 'Push Notifications', blurb: 'Delivered as device alerts.' },
  { key: 'email', masterKey: 'emailNotifications', title: 'Email Notifications', blurb: 'Delivered to your registered email.' },
]

const allOn = (): NotificationSettings => {
  const t = Object.fromEntries(NOTIFICATION_TYPES.map((k) => [k, true])) as Record<NotificationType, boolean>
  return { email: { ...t }, push: { ...t } }
}

export default function NotificationSettingsScreen() {
  const { user, refreshUser } = useAuth()
  const { showToast } = useToast()
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined

  const [pushNotifs, setPushNotifs] = useState(user?.pushNotifications ?? true)
  const [emailNotifs, setEmailNotifs] = useState(user?.emailNotifications ?? true)
  const [settings, setSettings] = useState<NotificationSettings>(user?.notificationSettings ?? allOn())
  const [saving, setSaving] = useState(false)
  // Adopt the authoritative `user`'s values in the SAME render it changes —
  // notably the null → cached hydration on a page reload, where useAuth starts
  // with user=null and lands the cached/real values a tick later. Doing this
  // DURING render (React's documented "adjust state when a prop changes"
  // pattern) rather than in a post-paint useEffect means the switches never
  // paint a default-on frame and then animate down to their saved off position
  // — the flicker. It converges immediately (the guard is false after syncing).
  // Optimistic toggles still win: a toggle sets local state, and refreshUser()
  // later lands the same persisted values here, so this never fights it.
  const [syncedUser, setSyncedUser] = useState(user)
  if (user && user !== syncedUser) {
    setSyncedUser(user)
    setPushNotifs(user.pushNotifications)
    setEmailNotifs(user.emailNotifications)
    setSettings(user.notificationSettings)
  }

  const masterValue = (ch: NotificationChannel) => (ch === 'push' ? pushNotifs : emailNotifs)
  const setMaster = (ch: NotificationChannel, v: boolean) =>
    ch === 'push' ? setPushNotifs(v) : setEmailNotifs(v)

  async function toggleMaster(ch: NotificationChannel, value: boolean) {
    if (saving) return
    setMaster(ch, value) // optimistic
    setSaving(true)
    try {
      const key = ch === 'push' ? 'pushNotifications' : 'emailNotifications'
      await apiPatch<UserResponse>('/api/v1/users/me', { [key]: value })
      await refreshUser()
    } catch (err: unknown) {
      setMaster(ch, !value) // roll back
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Flip every type in a group at once. The wire payload is still per-type, so
  // the server-side gate + merge are unchanged; the grouping lives only here.
  async function toggleGroup(ch: NotificationChannel, group: NotificationGroup, value: boolean) {
    if (saving) return
    const prev = settings // exact rollback target
    const patch = Object.fromEntries(group.types.map((t) => [t, value])) as Partial<NotificationTypeToggles>
    setSettings((cur) => ({ ...cur, [ch]: { ...cur[ch], ...patch } })) // optimistic
    setSaving(true)
    try {
      await apiPatch<UserResponse>('/api/v1/users/me', { notificationSettings: { [ch]: patch } })
      await refreshUser()
    } catch (err: unknown) {
      setSettings(prev) // roll back
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Until the authoritative user is available (briefly, on a cold reload), show
  // a loader rather than the default-on switches — otherwise the ones the user
  // turned off would paint on and then flicker down to off once `user` lands.
  if (!user) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#a01818" />
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/account')}
          accessibilityLabel="Back to general settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Notifications</Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{ padding: 16, gap: 16, alignItems: isLandscape ? 'center' : undefined }}
      >
        <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
          {CHANNELS.map((ch) => {
            const on = masterValue(ch.key)
            return (
              <View key={ch.key} className="bg-paper rounded-2xl overflow-hidden">
                {/* Master switch for the channel */}
                <View className="flex-row items-center justify-between px-4 py-4 gap-4">
                  <View className="flex-1">
                    <Text className="text-ink font-medium font-typed">{ch.title}</Text>
                    <Text className="text-ink-soft text-xs font-typed mt-1">{ch.blurb}</Text>
                  </View>
                  <Switch
                    value={on}
                    onValueChange={(v) => toggleMaster(ch.key, v)}
                    trackColor={{ true: '#a01818', false: '#4a5a2a' }}
                    thumbColor="#fff"
                    disabled={saving}
                    accessibilityLabel={ch.title}
                  />
                </View>

                {/* Grouped toggles — shown only when the channel is on. Hidden
                    (not deleted) when off, so values are preserved. */}
                {on && (
                  <View className="border-t border-olive">
                    {NOTIFICATION_GROUPS.map((group) => {
                      // A group reads "on" iff every type it covers is on.
                      const groupOn = group.types.every((t) => settings[ch.key][t] !== false)
                      // Multi-type groups show their members as a caption so the
                      // single switch's scope is obvious.
                      const caption =
                        group.types.length > 1
                          ? group.types.map((t) => NOTIFICATION_TYPE_LABELS[t]).join(' · ')
                          : null
                      return (
                        <View
                          key={group.key}
                          className="flex-row items-center justify-between px-4 py-3 border-t border-olive/40 gap-4"
                        >
                          <View className="flex-1">
                            <Text className="text-ink font-typed text-sm">{group.label}</Text>
                            {caption && (
                              <Text className="text-ink-soft font-typed text-xs mt-0.5">{caption}</Text>
                            )}
                          </View>
                          <Switch
                            value={groupOn}
                            onValueChange={(v) => toggleGroup(ch.key, group, v)}
                            trackColor={{ true: '#a01818', false: '#4a5a2a' }}
                            thumbColor="#fff"
                            disabled={saving}
                            accessibilityLabel={`${ch.title}: ${group.label}`}
                          />
                        </View>
                      )
                    })}
                  </View>
                )}
              </View>
            )
          })}

          <Text className="font-typed text-ink-soft text-xs italic px-1">
            // Turning a channel off silences every notification on it. Account emails
            (sign-in, password reset) are always sent.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}
